// Add-on "Promociones" (fidelización: tarjeta de sellos o puntos).
// Funciones puras compartidas por la vista de gestión y el TPV.
//
// Contrato de una promoción (API `/loyalty/programs`):
//   { id, name, description, isActive, threshold, rewardType,
//     rewardProductId, rewardPercent, countMenuComponents,
//     validFrom, validTo, stampExpiryDays, redeemGraceDays,
//     rewardExpiryDays, productIds[], categoryIds[] }

import { apiErrorText, hasApiErrorCode } from "src/utils/apiErrorCode";
import { getDescendantIds } from "src/utils/categoryTree";

export const PROMOTIONS_ADDON_NAME = "Promociones";
export const PROMOTIONS_ADDON_PRICE = 9.99;

export const REWARD_TYPES = Object.freeze({
  FREE_CHEAPEST: "FREE_CHEAPEST",
  FREE_PRODUCT: "FREE_PRODUCT",
  PERCENT: "PERCENT",
});

export const REWARD_TYPE_OPTIONS = [
  {
    value: REWARD_TYPES.FREE_CHEAPEST,
    label: "Una unidad gratis del grupo (la más barata)",
  },
  { value: REWARD_TYPES.FREE_PRODUCT, label: "Un producto concreto gratis" },
  { value: REWARD_TYPES.PERCENT, label: "% de descuento en una unidad" },
];

export const LEDGER_TYPE_LABELS = Object.freeze({
  ACCRUE: "Sellos ganados",
  REDEEM: "Recompensa canjeada",
  REVERSE: "Anulado (cobro deshecho)",
  ADJUST: "Ajuste manual",
  EXPIRE: "Caducado",
  MANUAL: "Consumición sumada a mano",
});

const plural = (n, one, many) => (Math.abs(Number(n)) === 1 ? one : many);

// ── Tipos de promoción: sellos (tarjeta) o puntos ──────────────────

export const PROGRAM_TYPES = Object.freeze({ STAMPS: "STAMPS", POINTS: "POINTS" });

export const POINTS_REWARD_TYPES = Object.freeze({
  MONEY_OFF: "MONEY_OFF",
  FREE_PRODUCT: "FREE_PRODUCT",
});

export const POINTS_REWARD_TYPE_OPTIONS = [
  { value: POINTS_REWARD_TYPES.MONEY_OFF, label: "Descuento en euros" },
  { value: POINTS_REWARD_TYPES.FREE_PRODUCT, label: "Un producto gratis" },
];

export function isPointsProgram(p) {
  return (p?.type || PROGRAM_TYPES.STAMPS) === PROGRAM_TYPES.POINTS;
}

// "100 puntos = 5 € de descuento" · "50 puntos = Café gratis".
export function pointsRewardLabel(reward, { products = [] } = {}) {
  if (!reward) return "";
  const cost = Number(reward.pointsCost) || 0;
  let what = reward.name;
  if (!what) {
    if (reward.rewardType === POINTS_REWARD_TYPES.FREE_PRODUCT) {
      const p = products.find((x) => x.id === reward.productId);
      what = `${reward.productName || p?.name || p?.nombre || "producto"} gratis`;
    } else {
      what = `${formatEuros(reward.amount).replace(",00", "")} de descuento`;
    }
  }
  return `${cost} ${plural(cost, "punto", "puntos")} = ${what}`;
}

// Frase de vista previa de una promoción de puntos.
//   "1 punto por cada euro · +5 puntos por cada café · 100 puntos = 5 € de descuento"
export function pointsPreviewSentence(program, lookups = {}) {
  const parts = [];
  const ratio = Number(program?.pointsPerEuro) || 0;
  if (ratio > 0) {
    parts.push(ratio === 1 ? "1 punto por cada euro" : `${String(ratio).replace(".", ",")} puntos por cada euro`);
  }
  for (const i of program?.items || []) {
    const b = Number(i.bonusPoints) || 0;
    if (b <= 0) continue;
    const name =
      i.name ||
      (i.productoId
        ? (lookups.products || []).find((x) => x.id === i.productoId)?.name ||
          (lookups.products || []).find((x) => x.id === i.productoId)?.nombre
        : (lookups.categories || []).find((x) => x.id === i.categoryId)?.name);
    parts.push(
      i.productoId
        ? `+${b} ${plural(b, "punto", "puntos")} por cada ${String(name || "producto").toLowerCase()}`
        : `+${b} ${plural(b, "punto", "puntos")} por unidad en ${name || "la categoría"}`,
    );
  }
  const rewards = (program?.rewards || []).filter((r) => r.isActive !== false);
  for (const r of [...rewards].sort((a, b) => (a.pointsCost || 0) - (b.pointsCost || 0))) {
    parts.push(pointsRewardLabel(r, lookups));
  }
  return parts.length ? parts.join(" · ") : "Define cómo se ganan los puntos y qué se puede canjear.";
}

// Validación específica de puntos → { field: mensaje }.
export function validatePointsProgram(p) {
  const errors = {};
  if (!p?.name || !p.name.trim()) errors.name = "Pon un nombre a la promoción.";
  const ratio = Number(p?.pointsPerEuro);
  const hasBonus = (p?.items || []).some((i) => (Number(i.bonusPoints) || 0) > 0);
  if (p?.pointsPerEuro !== "" && p?.pointsPerEuro != null && !(ratio >= 0 && ratio <= 1000)) {
    errors.pointsPerEuro = "Entre 0 y 1000.";
  } else if (!(ratio > 0) && !hasBonus) {
    errors.pointsPerEuro = "Indica los puntos por euro o puntos extra en algún producto.";
  }
  const rewards = p?.rewards || [];
  if (rewards.length === 0) errors.rewards = "Añade al menos una recompensa.";
  rewards.forEach((r, i) => {
    if (!(Number(r.pointsCost) >= 1)) errors[`reward${i}`] = "Los puntos deben ser 1 o más.";
    else if (r.rewardType === POINTS_REWARD_TYPES.MONEY_OFF && !(Number(r.amount) > 0)) {
      errors[`reward${i}`] = "Indica los euros de descuento.";
    } else if (r.rewardType === POINTS_REWARD_TYPES.FREE_PRODUCT && !r.productId) {
      errors[`reward${i}`] = "Elige el producto.";
    }
  });
  if (p?.validFrom && p?.validTo && new Date(p.validTo) < new Date(p.validFrom)) {
    errors.validTo = "La fecha de fin es anterior a la de inicio.";
  }
  return errors;
}

// Saldo de puntos: "Puntos casa · 120 puntos".
export function formatPointsBalance(balance) {
  if (!balance) return "";
  const pts = Number(balance.points ?? balance.stamps) || 0;
  return `${balance.programName || "Puntos"} · ${pts} ${plural(pts, "punto", "puntos")}`;
}

// Progreso hacia la recompensa de puntos más barata.
export function pointsProgressPercent(balance) {
  const pts = Number(balance?.points ?? balance?.stamps) || 0;
  const costs = (balance?.pointsRewards || []).map((r) => Number(r.pointsCost) || 0).filter((c) => c > 0);
  if (costs.length === 0) return 0;
  const cheapest = Math.min(...costs);
  return Math.min(100, Math.round((pts / cheapest) * 100));
}

// Sellos/puntos que sumará el ticket ("+3 sellos" / "+12 puntos").
export function ticketEarningsLabel(stamps, points) {
  const out = [];
  if (stamps > 0) out.push(`+${stamps} ${plural(stamps, "sello", "sellos")}`);
  if (points > 0) out.push(`+${points} ${plural(points, "punto", "puntos")}`);
  return out.join(" · ");
}

// ¿La compra actual completa la tarjeta? (5/10 + 5 cafés). El camarero
// elige aplicarla ahora o guardarla para la próxima.
export function ticketCompletesCard(balance, pendingUnits) {
  const threshold = Number(balance?.threshold) || 0;
  if (threshold <= 0) return false;
  if ((Number(balance?.rewardsAvailable) || 0) > 0) return false;
  const stamps = Number(balance?.stamps) || 0;
  return stamps + (Number(pendingUnits) || 0) >= threshold;
}

// Clave para "Guardar para la próxima" (no volver a ofrecerla en este ticket).
export function deferKey(sessionId, programId) {
  return `${sessionId || "fast"}:${programId}`;
}

// Nombre corto del grupo que da sellos, para frases ("cafés").
// Si hay una sola categoría usamos su nombre; si hay un solo producto, el
// del producto; en otro caso "productos de la promoción".
export function qualifyingLabel(program, { categories = [], products = [] } = {}) {
  const catIds = program?.categoryIds || [];
  const prodIds = program?.productIds || [];
  const nameOf = (list, id) => list.find((x) => x.id === id)?.name || list.find((x) => x.id === id)?.nombre;
  if (catIds.length === 1 && prodIds.length === 0) {
    const n = nameOf(categories, catIds[0]);
    if (n) return n.toLowerCase();
  }
  if (prodIds.length === 1 && catIds.length === 0) {
    const n = nameOf(products, prodIds[0]);
    if (n) return n.toLowerCase();
  }
  return "productos de la promoción";
}

// Plazos de la promoción en lenguaje llano (para la vista previa).
//   "Los sellos caducan a los 90 días." · "La recompensa se debe canjear
//   en 30 días." · "Tras el fin, 15 días para canjear."
export function expirySentences(program) {
  const out = [];
  const stamp = Number(program?.stampExpiryDays) || 0;
  const reward = Number(program?.rewardExpiryDays) || 0;
  const grace = Number(program?.redeemGraceDays) || 0;
  if (stamp > 0) out.push(`Cada sello caduca a los ${stamp} ${plural(stamp, "día", "días")}.`);
  if (reward > 0) {
    out.push(`Al completar la tarjeta, tiene ${reward} ${plural(reward, "día", "días")} para canjear la recompensa.`);
  }
  if (program?.validTo) {
    const end = new Date(program.validTo).toLocaleDateString("es-ES");
    out.push(
      grace > 0
        ? `Suma sellos hasta el ${end}; las recompensas se pueden canjear ${grace} ${plural(grace, "día", "días")} más.`
        : `Suma sellos y se canjea hasta el ${end}.`,
    );
  }
  return out;
}

// Frase completa con plazos: "Cada 10 cafés, 1 gratis. Cada sello…".
export function fullPreview(program, lookups = {}) {
  return [previewSentence(program, lookups), ...expirySentences(program)].join(" ");
}

// Frase de vista previa: "Cada 10 cafés, 1 café gratis".
export function previewSentence(program, lookups = {}) {
  const n = Number(program?.threshold) || 0;
  if (n < 1) return "Define cada cuántas unidades se gana la recompensa.";
  const group = qualifyingLabel(program, lookups);
  const head = `Cada ${n} ${group}`;
  switch (program?.rewardType) {
    case REWARD_TYPES.FREE_PRODUCT: {
      const p = (lookups.products || []).find((x) => x.id === program.rewardProductId);
      const name = p?.name || p?.nombre || "el producto elegido";
      return `${head}, ${name} gratis`;
    }
    case REWARD_TYPES.PERCENT: {
      const pct = Number(program.rewardPercent) || 0;
      return `${head}, ${pct}% de descuento en una unidad`;
    }
    default:
      return `${head}, 1 gratis (la unidad más barata)`;
  }
}

// Conjunto de categorías que cuentan (incluye subcategorías).
export function expandCategoryIds(categoryIds, categories) {
  const out = new Set();
  for (const id of categoryIds || []) {
    for (const d of getDescendantIds(categories || [], id)) out.add(d);
  }
  return out;
}

// ¿Este producto da sellos en la promoción?
export function productQualifies(program, product, expandedCategoryIds) {
  if (!program || !product) return false;
  if ((program.productIds || []).includes(product.id)) return true;
  const cat = product.idCategory ?? product.idCategoria ?? null;
  return cat != null && expandedCategoryIds?.has(cat);
}

// Promoción vigente en `now` (activa y dentro de las fechas).
export function isProgramActive(program, now = new Date()) {
  if (!program || program.isActive === false) return false;
  const t = now.getTime();
  if (program.validFrom && new Date(program.validFrom).getTime() > t) return false;
  if (program.validTo) {
    const end = new Date(program.validTo);
    end.setHours(23, 59, 59, 999);
    if (end.getTime() < t) return false;
  }
  return true;
}

// ¿Se pueden canjear recompensas en `now`? Como isProgramActive pero tras
// el fin se admiten los días de gracia (redeemGraceDays).
export function canRedeemProgram(program, now = new Date()) {
  if (!program || program.isActive === false) return false;
  const t = now.getTime();
  if (program.validFrom && new Date(program.validFrom).getTime() > t) return false;
  if (program.validTo) {
    const end = new Date(program.validTo);
    end.setDate(end.getDate() + (Number(program.redeemGraceDays) || 0));
    end.setHours(23, 59, 59, 999);
    if (end.getTime() < t) return false;
  }
  return true;
}

// Avisos de caducidad de un saldo (TPV y ficha del cliente).
export function balanceExpiryNotes(balance, now = new Date()) {
  if (!balance) return [];
  const fmt = (d) => new Date(d).toLocaleDateString("es-ES");
  const notes = [];
  if ((Number(balance.rewardsAvailable) || 0) > 0 && balance.nextRewardExpiry) {
    notes.push(`Canjea la recompensa antes del ${fmt(balance.nextRewardExpiry)}`);
  }
  if (balance.programActive === false && balance.canRedeem && balance.redeemUntil) {
    notes.push(`Promoción terminada: se puede canjear hasta el ${fmt(balance.redeemUntil)}`);
  }
  const expiring = Number(balance.expiringStamps) || 0;
  if (expiring > 0 && balance.nextExpiry && new Date(balance.nextExpiry).getTime() > now.getTime()) {
    notes.push(`${expiring} ${plural(expiring, "sello caduca", "sellos caducan")} el ${fmt(balance.nextExpiry)}`);
  }
  return notes;
}

// Mensaje al vincular el cliente con parte de la mesa ya cobrada.
export function stampsAddedMessage(n) {
  const k = Number(n) || 0;
  if (k <= 0) return "";
  return `Se han sumado ${k} ${plural(k, "sello", "sellos")} de lo ya cobrado`;
}

// Cobro reciente: ¿se le puede asociar el cliente? (sin cliente previo).
export function recentPaymentSelectable(payment) {
  return !!payment && !payment.alreadyLinkedCustomerId && !payment.alreadyLinkedCustomerName;
}

// "2 cafés" para la lista de cobros recientes.
export function recentPaymentQualifyingLabel(payment) {
  const items = payment?.qualifying || [];
  if (items.length === 0) return "Sin productos de promociones";
  return items
    .map((q) => `${q.units} ${plural(q.units, "sello", "sellos")} ${q.programName}`)
    .join(" · ");
}

// Precio unitario tras aplicar la recompensa.
export function rewardUnitPrice(program, unitPrice) {
  const price = Number(unitPrice) || 0;
  if (program?.rewardType === REWARD_TYPES.PERCENT) {
    const pct = Math.min(100, Math.max(0, Number(program.rewardPercent) || 0));
    return Math.round(price * (1 - pct / 100) * 100) / 100;
  }
  return 0;
}

// Valor de la recompensa (lo que se descuenta).
export function rewardValue(program, unitPrice) {
  const price = Number(unitPrice) || 0;
  return Math.round((price - rewardUnitPrice(program, price)) * 100) / 100;
}

// Líneas del carrito (sin enviar) que pueden recibir la recompensa.
//   - FREE_CHEAPEST / PERCENT: unidades de productos de la promoción.
//   - FREE_PRODUCT: unidades del producto de recompensa.
// Excluye líneas ya enviadas (`old`), ya canjeadas o vendidas al peso.
// Devuelve [{ index, line, unitPrice }] ordenado por precio ascendente
// (la primera es la sugerencia: la unidad más barata).
export function eligibleCartLines(program, cart, { products = [], categories = [] } = {}) {
  if (!program || !Array.isArray(cart)) return [];
  const expanded = expandCategoryIds(program.categoryIds, categories);
  const byId = new Map(products.map((p) => [p.id, p]));
  const out = [];
  cart.forEach((line, index) => {
    if (!line || line.old || line.loyaltyProgramId || line.pesoGramos) return;
    if ((Number(line.quantity) || 0) < 1) return;
    const ok =
      program.rewardType === REWARD_TYPES.FREE_PRODUCT
        ? line.id === program.rewardProductId
        : productQualifies(program, { ...(byId.get(line.id) || {}), id: line.id, idCategory: line.idCategory ?? byId.get(line.id)?.idCategory }, expanded);
    if (ok) out.push({ index, line, unitPrice: Number(line.precio) || 0 });
  });
  out.sort((a, b) => a.unitPrice - b.unitPrice);
  return out;
}

// Sellos que sumará el carrito sin enviar con esta promoción.
export function cartStampsPreview(program, cart, lookups = {}) {
  if (!program || !Array.isArray(cart)) return 0;
  const expanded = expandCategoryIds(program.categoryIds, lookups.categories);
  const byId = new Map((lookups.products || []).map((p) => [p.id, p]));
  let total = 0;
  for (const line of cart) {
    if (!line || line.old || line.loyaltyProgramId || line.pesoGramos) continue;
    const prod = { ...(byId.get(line.id) || {}), id: line.id };
    if (line.idCategory != null) prod.idCategory = line.idCategory;
    if (productQualifies(program, prod, expanded)) total += Number(line.quantity) || 0;
  }
  return total;
}

// Recompensas que aún se pueden aplicar en este ticket: las disponibles
// en el saldo menos las ya aplicadas (en servidor y en el carrito).
export function remainingRewards(balance, appliedCount = 0) {
  const available = Number(balance?.rewardsAvailable) || 0;
  return Math.max(0, available - (Number(appliedCount) || 0));
}

// "Café 7/10" · "Café 10/10 · 1 recompensa".
export function formatBalance(balance) {
  if (!balance) return "";
  const threshold = Number(balance.threshold) || 0;
  const stamps = Number(balance.stamps) || 0;
  const progress = threshold > 0 ? stamps % threshold : stamps;
  const rewards = Number(balance.rewardsAvailable) || 0;
  const base = `${balance.programName || "Promoción"} ${rewards > 0 && progress === 0 ? threshold : progress}/${threshold}`;
  if (rewards > 0) return `${base} · ${rewards} ${plural(rewards, "recompensa", "recompensas")}`;
  return base;
}

// Porcentaje de la barra de progreso hacia la siguiente recompensa.
export function progressPercent(balance) {
  const threshold = Number(balance?.threshold) || 0;
  if (threshold <= 0) return 0;
  const stamps = Number(balance?.stamps) || 0;
  if ((Number(balance?.rewardsAvailable) || 0) > 0 && stamps % threshold === 0) return 100;
  return Math.round(((stamps % threshold) / threshold) * 100);
}

export function stampsPreviewLabel(qty) {
  const n = Number(qty) || 0;
  if (n <= 0) return "";
  return `+${n} ${plural(n, "sello", "sellos")} con este ticket`;
}

// Cuerpo de POST /loyalty/redeem para una línea ya comandada. La línea se
// identifica por (pedidoId, productoId, counterProductId): sin productoId el
// servidor no la encuentra (LINE_NOT_FOUND).
export function redeemPayload({ sessionId, mesaId, suggestion }) {
  return {
    idSession: sessionId,
    idMesa: mesaId,
    programId: suggestion?.programId,
    pedidoId: suggestion?.pedidoId,
    productoId: suggestion?.productoId,
    counterProductId: suggestion?.counterProductId,
  };
}

export function suggestionLabel(suggestion) {
  if (!suggestion) return "";
  const name = suggestion.productName || "producto";
  if (suggestion.completesWithThisTicket) {
    if (suggestion.rewardType === REWARD_TYPES.PERCENT) {
      return `Este ticket completa la tarjeta: ${suggestion.rewardPercent || ""}% en 1 ${name.toLowerCase()}`;
    }
    return `Este ticket completa la tarjeta: 1 ${name.toLowerCase()} gratis`;
  }
  if (suggestion.rewardType === REWARD_TYPES.PERCENT) {
    return `${suggestion.rewardPercent || ""}% en 1 ${name.toLowerCase()} disponible`;
  }
  return `1 ${name.toLowerCase()} gratis disponible`;
}

// Validación del formulario de promoción → { field: mensaje }.
export function validateProgram(p) {
  const errors = {};
  if (!p?.name || !p.name.trim()) errors.name = "Pon un nombre a la promoción.";
  if (!(Number(p?.threshold) >= 1)) errors.threshold = "Debe ser 1 o más.";
  if (!(p?.productIds?.length || p?.categoryIds?.length)) {
    errors.items = "Elige al menos un producto o una categoría.";
  }
  if (p?.rewardType === REWARD_TYPES.FREE_PRODUCT && !p.rewardProductId) {
    errors.rewardProductId = "Elige el producto de regalo.";
  }
  if (p?.rewardType === REWARD_TYPES.PERCENT) {
    const pct = Number(p.rewardPercent);
    if (!(pct > 0 && pct <= 100)) errors.rewardPercent = "Entre 1 y 100.";
  }
  if (p?.validFrom && p?.validTo && new Date(p.validTo) < new Date(p.validFrom)) {
    errors.validTo = "La fecha de fin es anterior a la de inicio.";
  }
  if (p?.stampExpiryDays != null && p.stampExpiryDays !== "" && !(Number(p.stampExpiryDays) >= 1)) {
    errors.stampExpiryDays = "Déjalo vacío o pon 1 día o más.";
  }
  const days = (v) => v != null && v !== "" && !(Number(v) >= 1 && Number(v) <= 3650);
  if (days(p?.redeemGraceDays)) errors.redeemGraceDays = "Déjalo vacío o entre 1 y 3650 días.";
  if (days(p?.rewardExpiryDays)) errors.rewardExpiryDays = "Déjalo vacío o entre 1 y 3650 días.";
  return errors;
}

const LOYALTY_ERRORS = [
  ["NO_REWARD_AVAILABLE", "El cliente no tiene recompensas disponibles."],
  ["LINE_NOT_ELIGIBLE", "Ese producto no entra en la promoción."],
  ["LINE_PAID", "Esa línea ya está cobrada; no se puede aplicar la recompensa."],
  ["LINE_ALREADY_REDEEMED", "Esa línea ya tiene una recompensa aplicada."],
  ["PROGRAM_INACTIVE", "La promoción no está activa."],
  ["CUSTOMER_REQUIRED", "Selecciona primero el cliente."],
  ["ADDON_REQUIRED", "Necesitas el add-on «Promociones» para usar la fidelización."],
  ["REWARD_PRICE_MISMATCH", "La recompensa no coincide con la promoción. Vuelve a aplicarla."],
  ["REDEMPTION_NOT_FOUND", "Esa recompensa ya no existe; actualizamos la mesa."],
  ["LINE_NOT_REDEEMED", "Esa línea no tiene ninguna recompensa aplicada."],
  ["PROGRAM_NOT_FOUND", "La promoción ya no existe."],
  ["CUSTOMER_NOT_FOUND", "No encontramos ese cliente."],
  ["SESSION_NOT_FOUND", "La mesa ya no tiene una cuenta abierta."],
  ["SESSION_CLOSED", "La cuenta de esta mesa ya está cerrada. Usa «Añadir a un cobro reciente»."],
  ["ALREADY_LINKED", "Ese cobro ya tiene un cliente de fidelización."],
  ["PAYMENT_TOO_OLD", "Solo se pueden añadir sellos a cobros de hoy."],
  ["REWARD_NOT_FOUND", "Esa recompensa de puntos ya no existe."],
  ["TICKET_TOO_SMALL", "El pendiente de la mesa no llega al descuento. Comanda más productos o elige otra recompensa."],
];

export function loyaltyErrorCode(err) {
  const hit = LOYALTY_ERRORS.find(([code]) => hasApiErrorCode(err, code));
  return hit ? hit[0] : null;
}

export function loyaltyErrorMessage(err, fallback = "No se pudo completar la operación.") {
  const hit = LOYALTY_ERRORS.find(([code]) => hasApiErrorCode(err, code));
  if (hit) return hit[1];
  const text = apiErrorText(err);
  if (text && text.length < 200 && /\s/.test(text)) return text;
  return fallback;
}

export function formatEuros(v) {
  const n = Number(v) || 0;
  return `${n.toFixed(2).replace(".", ",")} €`;
}

/**
 * 2026-10-06 · ¿Se muestra la barra «Fidelización · Cobro reciente» del cobro?
 * Solo con el add-on Promociones Y alguna promoción activa (no pausada, no
 * eliminada, en vigor). Si el ticket ya tiene cliente de fidelización se deja
 * (para poder quitarlo o ver sus canjes) aunque ya no quede ninguna activa.
 * Mientras no han cargado las promociones no se muestra (evita el parpadeo).
 */
export function hasActiveLoyaltyPrograms(programs, now = new Date()) {
  return (programs || []).some((p) => p && !p.deletedAt && isProgramActive(p, now));
}

export function shouldShowLoyaltyBar({ addOnActive, programsLoaded, programs, hasCustomer, now = new Date() }) {
  if (!addOnActive) return false;
  if (hasCustomer) return true;
  if (!programsLoaded) return false;
  return hasActiveLoyaltyPrograms(programs, now);
}
